
import React, { useState, useEffect } from "react";
import { MapPin } from "lucide-react";
import {
  MapContainer,
  TileLayer,
  Circle,
  Popup,
  Marker,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "../Style/(POI)Dashboard.css"; // Make sure path is correct
import L from "leaflet";
import { useQuery, useQueryClient } from "@tanstack/react-query";

// Fix default marker icons for Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const API_URL = "https://api.yellowmatrix.co.in";

// Auto-fit map to AOI bounds
const FitBounds = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [bounds, map]);
  return null;
};

// Focus map on selected AOI
const FocusOnSelectedAOI = ({ selectedAOI }) => {
  const map = useMap();
  useEffect(() => {
    if (selectedAOI) {
      const center = [selectedAOI.CenterLatitude, selectedAOI.CenterLongitude];
      map.flyTo(center, 14);
    }
  }, [selectedAOI, map]);
  return null;
};

const AOIDashboard = () => {
  const queryClient = useQueryClient();
  const [selectedAOI, setSelectedAOI] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Create State
  const [showCreatePage, setShowCreatePage] = useState(false);
  const [formData, setFormData] = useState({
    customerid: "",
    geofencename: "",
    centerlatitude: "",
    centerlongitude: "",
    radius: "",
    speedlimit: "",
    timelimit: "",
  });
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [errorCreate, setErrorCreate] = useState("");
  const [successCreate, setSuccessCreate] = useState("");

  // Edit State
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [errorUpdate, setErrorUpdate] = useState("");

  // Geocoding State
  const [isFetchingCoords, setIsFetchingCoords] = useState(false);
  const [customers, setCustomers] = useState([]);

  const token = localStorage.getItem("token"); // JWT token
  const userRole = localStorage.getItem("userRole");

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // inside AOIDashboard component
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Reset edit state if a new AOI is selected on the map
  useEffect(() => {
    setIsEditing(false);
    setErrorUpdate("");
  }, [selectedAOI]);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const res = await fetch(`${API_URL}/customers`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) {
          throw new Error("Failed to fetch customers");
        }

        const data = await res.json();
        setCustomers(data);
      } catch (error) {
        console.error(error);
      }
    };

    fetchCustomers(); // ✅ call only once here
  }, [token]);

  // Fetch AOI list
  const fetchAoiList = async () => {
    if (!token) throw new Error("Authentication token not found.");
    const res = await fetch(`${API_URL}/aoi?limit=100`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || "Failed to fetch AOIs list");
    }
    const json = await res.json();
    return json.data || [];
  };

  // Fetch single AOI details
  const fetchAoiDetail = async (geofenceId) => {
    if (!token) throw new Error("Authentication token not found.");
    const res = await fetch(`${API_URL}/aoi/${geofenceId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || `Failed to fetch AOI ${geofenceId}`);
    }
    return res.json();
  };

  const {
    data: aoiList = [],
    isLoading: isLoadingAoiList,
    error: aoiListError,
  } = useQuery({
    queryKey: ["aoiList", token],
    queryFn: fetchAoiList,
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: aoiDetails = [],
    isLoading: isLoadingAoiDetails,
    error: aoiDetailsError,
  } = useQuery({
    queryKey: ["aoiDetails", aoiList, token],
    queryFn: async () => {
      if (!aoiList || aoiList.length === 0) return [];
      const details = await Promise.all(
        aoiList.map((aoi) => fetchAoiDetail(aoi.GeofenceID)),
      );
      return details;
    },
    enabled: !!aoiList.length && !!token,
    staleTime: 5 * 60 * 1000,
  });

  const isLoading = isLoadingAoiList || isLoadingAoiDetails;
  const error = aoiListError || aoiDetailsError;

  const calculateBounds = () => {
    if (!aoiDetails || aoiDetails.length === 0) return null;
    return aoiDetails.reduce(
      (bounds, aoi) => {
        const lat = aoi.CenterLatitude;
        const lng = aoi.CenterLongitude;
        const radiusDeg = (aoi.Radius || 0) / 111.32;
        return [
          [
            Math.min(bounds[0][0], lat - radiusDeg),
            Math.min(
              bounds[0][1],
              lng - radiusDeg / Math.cos((lat * Math.PI) / 180),
            ),
          ],
          [
            Math.max(bounds[1][0], lat + radiusDeg),
            Math.max(
              bounds[1][1],
              lng + radiusDeg / Math.cos((lat * Math.PI) / 180),
            ),
          ],
        ];
      },
      [
        [Infinity, Infinity],
        [-Infinity, -Infinity],
      ],
    );
  };

  const bounds = calculateBounds();
  const defaultCenter = bounds
    ? [(bounds[0][0] + bounds[1][0]) / 2, (bounds[0][1] + bounds[1][1]) / 2]
    : [20.5937, 78.9629]; // Default roughly to India

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // const filteredAOIs = aoiList.filter((aoi) =>
  //   aoi.GeofenceName.toLowerCase().includes(searchTerm.toLowerCase()),
  // );
  const filteredAOIs = aoiList.filter((aoi) =>
    (aoi.GeofenceName || "").toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleBackToList = () => {
    setSelectedAOI(null);
    setIsEditing(false);
  };

  const handleCreateChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // -------------------------
  // GEOCODING HANDLER
  // -------------------------
  const handleFetchCoordinates = async (locationName, isEditMode = false) => {
    if (!locationName) {
      alert("Please enter a location name first.");
      return;
    }

    setIsFetchingCoords(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          locationName,
        )}`,
      );
      const data = await response.json();

      if (data && data.length > 0) {
        // Grab the best match
        const { lat, lon } = data[0];

        if (isEditMode) {
          setEditFormData((prev) => ({
            ...prev,
            centerlatitude: parseFloat(lat).toFixed(6),
            centerlongitude: parseFloat(lon).toFixed(6),
          }));
        } else {
          setFormData((prev) => ({
            ...prev,
            centerlatitude: parseFloat(lat).toFixed(6),
            centerlongitude: parseFloat(lon).toFixed(6),
          }));
        }
      } else {
        alert(
          "Could not find coordinates for this location. Please try a more specific name.",
        );
      }
    } catch (err) {
      console.error("Geocoding failed:", err);
      alert("Failed to fetch coordinates due to a network error.");
    } finally {
      setIsFetchingCoords(false);
    }
  };

  // -------------------------
  // CREATE HANDLER
  // -------------------------
  const handleCreateAOI = async (e) => {
    e.preventDefault();
    setLoadingCreate(true);
    setErrorCreate("");
    setSuccessCreate("");

    try {
      const res = await fetch(`${API_URL}/aoi`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          customerid: formData.customerid,
          geofencename: formData.geofencename,
          areatype: "circle",
          centerlatitude: parseFloat(formData.centerlatitude),
          centerlongitude: parseFloat(formData.centerlongitude),
          radius: parseFloat(formData.radius),
          speedlimit: parseFloat(formData.speedlimit),
          timelimit: parseFloat(formData.timelimit),
        }),
      });

      const data = await res.json();
      if (data.errors) throw new Error(data.errors.join(", "));
      if (!res.ok) throw new Error(data.error || "Failed to create AOI");

      setSuccessCreate("AOI Created Successfully ✅");
      setFormData({
        customerid: "",
        geofencename: "",
        centerlatitude: "",
        centerlongitude: "",
        radius: "",
        speedlimit: "",
        timelimit: "",
      });
      queryClient.invalidateQueries(["aoiList", token]);
      setTimeout(() => setShowCreatePage(false), 1500); // Auto-close after 1.5s
    } catch (err) {
      setErrorCreate(err.message);
    } finally {
      setLoadingCreate(false);
    }
  };

  const executeDeleteAOI = async () => {
    setIsDeleting(true);
    try {
      const res = await fetch(`${API_URL}/aoi/${selectedAOI.GeofenceID}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Failed to delete AOI");
      }

      // Reset states on success
      setSelectedAOI(null);
      setShowDeleteModal(false);

      queryClient.invalidateQueries(["aoiList"]);
      queryClient.invalidateQueries(["aoiDetails"]);
    } catch (err) {
      alert(err.message);
    } finally {
      setIsDeleting(false);
    }
  };
  // -------------------------
  // EDIT HANDLERS
  // -------------------------
  const handleEditClick = () => {
    setEditFormData({
      geofencename: selectedAOI.GeofenceName,
      centerlatitude: selectedAOI.CenterLatitude,
      centerlongitude: selectedAOI.CenterLongitude,
      radius: selectedAOI.Radius,
      speedlimit: selectedAOI.SpeedLimit || 0,
      timelimit: selectedAOI.TimeLimit || 0,
    });
    setIsEditing(true);
  };

  const handleEditChange = (e) => {
    setEditFormData({ ...editFormData, [e.target.name]: e.target.value });
  };

  const handleUpdateAOI = async (e) => {
    e.preventDefault();
    setLoadingUpdate(true);
    setErrorUpdate("");

    try {
      const res = await fetch(`${API_URL}/aoi/${selectedAOI.GeofenceID}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          geofencename: editFormData.geofencename,
          centerlatitude: parseFloat(editFormData.centerlatitude),
          centerlongitude: parseFloat(editFormData.centerlongitude),
          radius: parseFloat(editFormData.radius),
          speedlimit: parseFloat(editFormData.speedlimit),
          timelimit: parseFloat(editFormData.timelimit),
        }),
      });

      const data = await res.json();
      if (data.errors) throw new Error(data.errors.join(", "));
      if (!res.ok) throw new Error(data.error || "Failed to update AOI");

      // Update local state instantly to reflect on UI immediately
      setSelectedAOI({
        ...selectedAOI,
        GeofenceName: editFormData.geofencename,
        CenterLatitude: parseFloat(editFormData.centerlatitude),
        CenterLongitude: parseFloat(editFormData.centerlongitude),
        Radius: parseFloat(editFormData.radius),
        SpeedLimit: parseFloat(editFormData.speedlimit),
        TimeLimit: parseFloat(editFormData.timelimit),
      });

      // Refetch background data
      queryClient.invalidateQueries(["aoiList", token]);
      queryClient.invalidateQueries(["aoiDetails", token]);

      setIsEditing(false); // Close edit mode
    } catch (err) {
      setErrorUpdate(err.message);
    } finally {
      setLoadingUpdate(false);
    }
  };

  return (
    <div className="aoi-dashboard">
      {/* Create Modal/Page */}
      {showCreatePage && (
        <div className="create-aoi-page">
          <h2>Create AOI</h2>

          <form onSubmit={handleCreateAOI}>
            {userRole === "SuperAdmin" && (
              <select
                name="customerid"
                value={formData.CustomerID}
                onChange={handleCreateChange}
                required
              >
                <option value="">Select Customer</option>

                {customers.map((cust) => (
                  <option key={cust.CustomerID} value={cust.CustomerID}>
                    {cust.CustomerName}
                  </option>
                ))}
              </select>
            )}

            <div className="location-fetch-wrapper">
              <input
                name="geofencename"
                placeholder="Area Name / Location"
                value={formData.geofencename}
                onChange={handleCreateChange}
                required
              />
              <button
                type="button"
                className="fetch-coords-btn"
                onClick={() =>
                  handleFetchCoordinates(formData.geofencename, false)
                }
                disabled={isFetchingCoords}
                title="Fetch Latitude & Longitude"
              >
                {isFetchingCoords ? "..." : <MapPin size={20} />}
              </button>
            </div>

            <input
              name="centerlatitude"
              placeholder="Latitude"
              value={formData.centerlatitude}
              onChange={handleCreateChange}
              required
              type="number"
              step="any"
            />
            <input
              name="centerlongitude"
              placeholder="Longitude"
              value={formData.centerlongitude}
              onChange={handleCreateChange}
              required
              type="number"
              step="any"
            />
            <input
              name="radius"
              placeholder="Radius (km)"
              value={formData.radius}
              onChange={handleCreateChange}
              required
              type="number"
              step="any"
            />
            <input
              name="speedlimit"
              placeholder="Speed Limit"
              value={formData.speedlimit}
              onChange={handleCreateChange}
              required
              type="number"
            />
            <input
              name="timelimit"
              placeholder="Time Limit"
              value={formData.timelimit}
              onChange={handleCreateChange}
              required
              type="number"
            />
            <div className="btn-group">
              <button type="submit" disabled={loadingCreate}>
                {loadingCreate ? "Creating..." : "Create"}
              </button>
              <button type="button" onClick={() => setShowCreatePage(false)}>
                Cancel
              </button>
            </div>
            {errorCreate && (
              <p style={{ color: "red", marginTop: "10px" }}>{errorCreate}</p>
            )}
            {successCreate && (
              <p style={{ color: "green", marginTop: "10px" }}>
                {successCreate}
              </p>
            )}
          </form>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="delete-modal-overlay">
          <div className="delete-modal-box">
            <div className="delete-icon-wrapper">
              <i className="fa-solid fa-triangle-exclamation"></i>
            </div>
            <h3>Are you sure?</h3>
            <p>
              Do you really want to delete{" "}
              <strong>{selectedAOI?.GeofenceName}</strong>? This action cannot
              be undone.
            </p>
            <div className="delete-modal-actions">
              <button
                className="cancel-btn"
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="confirm-delete-btn"
                onClick={executeDeleteAOI}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="aoi-header">
        <h1 className="aoi-title">Area of Interest (AOI) Dashboard</h1>
        <p className="aoi-subtitle">
          Centralized dashboard for AOI creation, mapping, and monitoring.
        </p>
        <hr className="line-aoi" width="100%" />
      </div>

      <div className="create-btn-aoi">
        <button
          className="create-aoi-btn"
          onClick={() => setShowCreatePage(true)}
        >
          CREATE AOI
        </button>
      </div>

      <div className="aoi-grid">
        {/* Left Panel */}
        <div className="card-aoi">
          <div className="card-content-aoi">
            {selectedAOI ? (
              <div>
                <div className="aoi-details-header">
                  {isEditing ? "EDIT AOI" : "AOI DETAILS"}
                </div>

                {isEditing ? (
                  /* ------------- EDIT FORM ------------- */
                  <form onSubmit={handleUpdateAOI} className="edit-aoi-form">
                    <div className="edit-form-group">
                      <label>Area Name / Location</label>
                      <div className="location-fetch-wrapper">
                        <input
                          name="geofencename"
                          value={editFormData.geofencename}
                          onChange={handleEditChange}
                          required
                        />
                        <button
                          type="button"
                          className="fetch-coords-btn"
                          onClick={() =>
                            handleFetchCoordinates(
                              editFormData.geofencename,
                              true,
                            )
                          }
                          disabled={isFetchingCoords}
                          title="Fetch Latitude & Longitude"
                        >
                          {isFetchingCoords ? "..." : <MapPin size={18} />}
                        </button>
                      </div>
                    </div>
                    <div className="edit-form-row">
                      <div className="edit-form-group">
                        <label>Latitude</label>
                        <input
                          name="centerlatitude"
                          value={editFormData.centerlatitude}
                          onChange={handleEditChange}
                          required
                          type="number"
                          step="any"
                        />
                      </div>
                      <div className="edit-form-group">
                        <label>Longitude</label>
                        <input
                          name="centerlongitude"
                          value={editFormData.centerlongitude}
                          onChange={handleEditChange}
                          required
                          type="number"
                          step="any"
                        />
                      </div>
                    </div>
                    <div className="edit-form-group">
                      <label>Radius (km)</label>
                      <input
                        name="radius"
                        value={editFormData.radius}
                        onChange={handleEditChange}
                        required
                        type="number"
                        step="any"
                      />
                    </div>
                    <div className="edit-form-row">
                      <div className="edit-form-group">
                        <label>Speed Limit</label>
                        <input
                          name="speedlimit"
                          value={editFormData.speedlimit}
                          onChange={handleEditChange}
                          required
                          type="number"
                        />
                      </div>
                      <div className="edit-form-group">
                        <label>Time Limit</label>
                        <input
                          name="timelimit"
                          value={editFormData.timelimit}
                          onChange={handleEditChange}
                          required
                          type="number"
                        />
                      </div>
                    </div>

                    {errorUpdate && (
                      <p
                        className="error-aoi"
                        style={{ color: "red", fontSize: "14px" }}
                      >
                        {errorUpdate}
                      </p>
                    )}

                    <div className="actions-aoi edit-actions">
                      <button
                        type="submit"
                        className="save-btn"
                        disabled={loadingUpdate}
                      >
                        {loadingUpdate ? (
                          "Saving..."
                        ) : (
                          <>
                            <i className="fa-solid fa-check"></i> Save
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        className="cancel-btn"
                        onClick={() => setIsEditing(false)}
                      >
                        <i className="fa-solid fa-xmark"></i> Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  /* ------------- VIEW MODE ------------- */
                  <div className="aoi-details-content">
                    <div className="aoi-detail-item">
                      <i className="fa-solid fa-location-dot aoi-detail-icon"></i>
                      <div className="aoi-detail-text">
                        <span className="aoi-detail-label">Area name</span>
                        <span className="aoi-detail-value">
                          {selectedAOI.GeofenceName}
                        </span>
                      </div>
                    </div>
                    <div className="aoi-detail-item">
                      <i className="fa-solid fa-shapes aoi-detail-icon"></i>
                      <div className="aoi-detail-text">
                        <span className="aoi-detail-label">Area type</span>
                        <span className="aoi-detail-value">
                          {selectedAOI.AreaType}
                        </span>
                      </div>
                    </div>
                    <div className="aoi-detail-item">
                      <i className="fa-solid fa-calendar-days aoi-detail-icon"></i>
                      <div className="aoi-detail-text">
                        <span className="aoi-detail-label">
                          {selectedAOI.UpdatedOn
                            ? "Last Updated"
                            : "Created On"}
                        </span>

                        <span className="aoi-detail-value">
                          {formatDate(
                            selectedAOI.UpdatedOn || selectedAOI.CreatedOn,
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="aoi-detail-item">
                      <i className="fa-solid fa-ruler aoi-detail-icon"></i>
                      <div className="aoi-detail-text">
                        <span className="aoi-detail-label">Radius</span>
                        <span className="aoi-detail-value">
                          {selectedAOI.Radius} km
                        </span>
                      </div>
                    </div>
                    <div className="actions-aoi">
                      <button className="action-btn" onClick={handleBackToList}>
                        <i className="fa-solid fa-arrow-left"></i> Back to list
                      </button>
                      <div className="space-x-2">
                        <button
                          className="action-btn"
                          title="Delete"
                          onClick={() => setShowDeleteModal(true)} // <-- Update this line
                        >
                          <i className="fa-solid fa-trash"></i>
                        </button>
                        <button
                          className="action-btn"
                          title="Edit"
                          onClick={handleEditClick}
                        >
                          <i className="fa-solid fa-pen-to-square"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* ------------- LIST MODE ------------- */
              <>
                <div className="aoi-details-header">AOI DETAILS</div>
                <div className="search-container-aoi">
                  <i className="fa-solid fa-magnifying-glass search-icon-aoi"></i>
                  <input
                    type="text"
                    placeholder="Search AOI"
                    className="search-input"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <div className="aoi-table-container">
                  <table className="aoi-table">
                    <thead className="aoi-table-header">
                      <tr>
                        <th>Area Name</th>
                        <th>Created On</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAOIs.length > 0 ? (
                        filteredAOIs.map((aoi) => (
                          <tr
                            key={aoi.GeofenceID}
                            className={`aoi-table-row`}
                            onClick={() => {
                              const detail = aoiDetails.find(
                                (d) => d.GeofenceID === aoi.GeofenceID,
                              );
                              setSelectedAOI(detail);
                            }}
                          >
                            <td>{aoi.GeofenceName}</td>
                            <td>{formatDate(aoi.CreatedOn)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan="2"
                            className="no-aois-found"
                            style={{ textAlign: "center", padding: "15px" }}
                          >
                            No AOIs found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  <div className="aoi-pagination">
                    <button className="pagination-btn" disabled>
                      &#60;
                    </button>
                    <span>
                      1 - {filteredAOIs.length} of {aoiList.length}
                    </span>
                    <button className="pagination-btn" disabled>
                      &#62;
                    </button>
                  </div>
                </div>

                {isLoading && (
                  <div className="loading-aoi">Loading AOIs...</div>
                )}
                {error && (
                  <div
                    className="error-aoi"
                    style={{ color: "red", padding: "10px" }}
                  >
                    Error: {error.message}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Right Panel - Map */}
        <div className={`card-aoi ${!isMobile ? "map-card" : ""}`}>
          <div className="map-container-aoi">
            <MapContainer
              center={defaultCenter}
              zoom={bounds ? 12 : 5}
              style={{ height: "100%", width: "100%" }}
            >
              <FitBounds bounds={bounds} />
              <FocusOnSelectedAOI selectedAOI={selectedAOI} />
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution="&copy; OpenStreetMap contributors"
              />

              {aoiDetails.map((aoi) => (
                <React.Fragment key={aoi.GeofenceID}>
                  <Marker
                    position={[aoi.CenterLatitude, aoi.CenterLongitude]}
                    eventHandlers={{ click: () => setSelectedAOI(aoi) }}
                  >
                    <Popup>
                      <div>
                        <strong>{aoi.GeofenceName}</strong>
                        <br />
                        Center: {aoi.CenterLatitude?.toFixed(6) || "N/A"},{" "}
                        {aoi.CenterLongitude?.toFixed(6) || "N/A"}
                        <br />
                        Radius: {aoi.Radius || "N/A"} km
                      </div>
                    </Popup>
                  </Marker>
                  <Circle
                    center={[aoi.CenterLatitude, aoi.CenterLongitude]}
                    radius={(aoi.Radius || 0) * 1000} // Convert km to meters for Leaflet
                    color={
                      selectedAOI?.GeofenceID === aoi.GeofenceID
                        ? "#ec4899"
                        : "#3b82f6"
                    }
                    fillColor={
                      selectedAOI?.GeofenceID === aoi.GeofenceID
                        ? "#f9a8d4"
                        : "#93c5fd"
                    }
                    fillOpacity={0.4}
                    eventHandlers={{ click: () => setSelectedAOI(aoi) }}
                  />
                </React.Fragment>
              ))}
            </MapContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AOIDashboard;
